// src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchCurrentUser, logoutUser } from "../services/api";
import { queryCache } from "../lib/queryCache";

const AuthContext = createContext(null);

// ✅ normalize response so user is ALWAYS the user object
function normalizeUser(payload) {
  // payload could be: { success, data: user } OR user directly
  if (!payload) return null;
  if (payload.data && typeof payload.data === "object") return payload.data;
  if (payload.user && typeof payload.user === "object") return payload.user;
  return payload;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // runs on app load/refresh
  useEffect(() => {
    let mounted = true;

    const boot = async () => {
      try {
        const res = await fetchCurrentUser();
        const u = normalizeUser(res);
        if (mounted) setUser(u);
      } catch (e) {
        if (mounted) setUser(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    boot();
    return () => {
      mounted = false;
    };
  }, []);

  // called after login API succeeds
  const login = (payload) => {
    setUser(normalizeUser(payload));
  };

  const logout = async () => {
    try {
      await logoutUser();
    } catch (_) {
      // ignore
    } finally {
      queryCache.invalidateAll();
      localStorage.removeItem("jwt_token");
      setUser(null);
    }
  };

  // ✅ expose setter so Profile can update user after avatar save
  const setAuthUser = (updater) => {
    setUser((prev) => (typeof updater === "function" ? updater(prev) : updater));
  };

  const value = useMemo(
    () => ({ user, loading, login, logout, setAuthUser }),
    [user, loading]
  );

  // ✅ IMPORTANT: don't render app until auth check finished
  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
